import React from "react";

// Silhouette avatars: a gradient head and shoulders with a lighter rim, on a
// soft tinted circle. The color family and the head's squared corner come from
// the user's id, so a user's avatar is identical every time and never changes
// when they are renamed or given a different role.

// [gradient start, gradient end, rim, background tint]
const PALETTES = [
  ["#7a60ff", "#b9aeff", "#cdc5ff", "#f1eeff"],
  ["#3b82f6", "#93c5fd", "#bcd9fe", "#eaf2ff"],
  ["#14b8a6", "#7ee3d6", "#b2ede5", "#e6f8f5"],
  ["#f0587c", "#ffa5bb", "#ffc6d4", "#ffeef2"],
  ["#f59e0b", "#fcd37a", "#fde3a6", "#fff6e3"],
  ["#22a06b", "#86dcae", "#b8ebd0", "#e8f7ef"],
  ["#4f46e5", "#a5a0f5", "#c6c2fa", "#eeedfe"],
  ["#f26b3a", "#ffb08e", "#ffcfba", "#fff0ea"],
];

function hash(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function avatarTraits(seed) {
  const h = hash(String(seed || "user"));
  const palette = PALETTES[h % PALETTES.length];
  return {
    from: palette[0],
    to: palette[1],
    rim: palette[2],
    tint: palette[3],
    // Which side carries the squared corner on the head.
    mirror: ((h >>> 5) & 1) === 1,
  };
}

const HEAD = "M20.5 25C20.5 16.5 25.5 11.5 32 11.5H39.5Q43 11.5 43 15V25C43 32 38 36 32 36C26 36 20.5 32 20.5 25Z";
const BODY = "M10 64C10 49 20 42.5 32 42.5C44 42.5 54 49 54 64Z";

export default function UserAvatar({ id, name, size = 40, className = "" }) {
  const t = avatarTraits(id || name);
  const uid = `av-${hash(String(id || name || "user"))}`;

  return (
    <svg
      className={`user-avatar ${className}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={name ? `${name}'s avatar` : "User avatar"}
    >
      <defs>
        <clipPath id={`${uid}-clip`}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
        <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={t.from} />
          <stop offset="1" stopColor={t.to} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${uid}-clip)`}>
        <rect width="64" height="64" fill={t.tint} />
        <g stroke={t.rim} strokeWidth="3" strokeLinejoin="round" paintOrder="stroke" fill={`url(#${uid}-fill)`}>
          <path d={BODY} />
          <path d={HEAD} transform={t.mirror ? "translate(64 0) scale(-1 1)" : undefined} />
        </g>
      </g>
    </svg>
  );
}
